import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, scrollToId, waLink, ease } from "./constants";
import { WhatsAppIcon, LogoMark } from "./Icons";

const Brand = () => (
  <button onClick={() => scrollToId("inicio")} data-testid="header-logo" className="flex items-center gap-3 group">
    <span className="transition-transform duration-500 group-hover:rotate-[20deg]"><LogoMark className="h-11 w-11 sm:h-12 sm:w-12" /></span>
    <span className="text-left leading-none">
      <span className="font-display block text-2xl font-black tracking-wide text-white">DESTAK</span>
      <span className="font-mono-x block text-[10px] uppercase tracking-[0.2em] text-[#FACC15]">Compensados & Madeiras</span>
    </span>
  </button>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 50);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.1, ease }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${scrolled ? "bg-[#0c0a09]/85 backdrop-blur-xl border-white/10 py-2" : "bg-transparent border-transparent py-4"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
        <Brand />
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`} className="nav-link font-mono-x text-xs uppercase tracking-[0.2em] text-stone-200 hover:text-[#FACC15] transition-colors">
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <span className="hidden sm:block">
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn" className="btn-yellow">
              <WhatsAppIcon className="h-4 w-4" /> Fale no WhatsApp
            </a>
          </span>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn-mobile" aria-label="WhatsApp" className="sm:hidden grid h-11 w-11 place-items-center rounded-full bg-[#FACC15] text-[#0c0a09]">
            <WhatsAppIcon className="h-5 w-5" />
          </a>
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Abrir menu" className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "circle(0% at 95% 4%)" }}
            animate={{ clipPath: "circle(150% at 95% 4%)" }}
            exit={{ clipPath: "circle(0% at 95% 4%)" }}
            transition={{ duration: 0.7, ease }}
            className="fixed inset-0 z-[60] flex h-[100svh] flex-col bg-[#FACC15] px-6 py-5 text-[#0c0a09]"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-3xl font-black">DESTAK</span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Fechar menu" className="grid h-11 w-11 place-items-center rounded-full bg-[#0c0a09] text-[#FACC15]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-12 flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + i * 0.06, duration: 0.5, ease }}
                  onClick={() => go(n.id)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="flex items-baseline gap-4 border-b border-black/15 py-3 text-left font-display text-5xl font-black uppercase"
                >
                  <span className="font-mono-x text-xs">0{i + 1}</span>
                  {n.label}
                </motion.button>
              ))}
            </nav>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="mt-auto flex items-center justify-center gap-3 rounded-full bg-[#0c0a09] py-4 font-display text-xl font-bold uppercase tracking-wide text-[#FACC15]">
              <WhatsAppIcon className="h-5 w-5" /> Fale no WhatsApp
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
